// Primary 4 English Learning Data - Part 2 (Topics 11-18)
// Based on Primary English Learning P4 Topics.txt

export const p4DataPart2 = {
  topics: [
    {
      id: 'p4-topic-11',
      title: 'Relative Clauses',
      description: 'Learning to use relative clauses with "who", "which", "that"',
      purpose: 'To teach students how to use relative clauses to provide additional information about nouns using relative pronouns.',
      questions: [
        {
          question: 'The man _____ lives next door is a doctor.',
          answer: 'who',
          explanation: 'Use "who" to refer to people in relative clauses.'
        },
        {
          question: 'The book _____ I bought is very interesting.',
          answer: 'which',
          explanation: 'Use "which" to refer to things in relative clauses.'
        },
        {
          question: 'The car _____ is parked outside is red.',
          answer: 'that',
          explanation: 'Use "that" to refer to things in relative clauses.'
        },
        {
          question: 'The girl _____ is wearing a blue dress is my sister.',
          answer: 'who',
          explanation: 'Use "who" to refer to people in relative clauses.'
        },
        {
          question: 'The movie _____ we watched was exciting.',
          answer: 'which',
          explanation: 'Use "which" to refer to things in relative clauses.'
        },
        {
          question: 'The dog _____ barks loudly belongs to my neighbor.',
          answer: 'that',
          explanation: 'Use "that" to refer to animals in relative clauses.'
        },
        {
          question: 'The teacher _____ teaches English is very kind.',
          answer: 'who',
          explanation: 'Use "who" to refer to people in relative clauses.'
        },
        {
          question: 'The house _____ we live in is old.',
          answer: 'which',
          explanation: 'Use "which" to refer to things in relative clauses.'
        },
        {
          question: 'The cat _____ is sleeping on the sofa is mine.',
          answer: 'that',
          explanation: 'Use "that" to refer to animals in relative clauses.'
        },
        {
          question: 'The boy _____ is playing football is my friend.',
          answer: 'who',
          explanation: 'Use "who" to refer to people in relative clauses.'
        }
      ]
    },
    {
      id: 'p4-topic-12',
      title: 'Gerunds and Infinitives',
      description: 'Learning to use gerunds and infinitives after different verbs',
      purpose: 'To teach students when to use gerunds (-ing form) and infinitives (to + base form) after different verbs.',
      questions: [
        {
          question: 'I enjoy _____ (read) books.',
          answer: 'reading',
          explanation: 'Use gerund (-ing form) after "enjoy".'
        },
        {
          question: 'She wants _____ (go) to the park.',
          answer: 'to go',
          explanation: 'Use infinitive (to + base form) after "want".'
        },
        {
          question: 'He likes _____ (play) football.',
          answer: 'playing',
          explanation: 'Use gerund (-ing form) after "like".'
        },
        {
          question: 'We need _____ (study) for the exam.',
          answer: 'to study',
          explanation: 'Use infinitive (to + base form) after "need".'
        },
        {
          question: 'They love _____ (watch) movies.',
          answer: 'watching',
          explanation: 'Use gerund (-ing form) after "love".'
        },
        {
          question: 'I plan _____ (visit) my grandmother.',
          answer: 'to visit',
          explanation: 'Use infinitive (to + base form) after "plan".'
        },
        {
          question: 'She hates _____ (cook) dinner.',
          answer: 'cooking',
          explanation: 'Use gerund (-ing form) after "hate".'
        },
        {
          question: 'He hopes _____ (pass) the test.',
          answer: 'to pass',
          explanation: 'Use infinitive (to + base form) after "hope".'
        },
        {
          question: 'I prefer _____ (walk) to school.',
          answer: 'walking',
          explanation: 'Use gerund (-ing form) after "prefer".'
        },
        {
          question: 'They decide _____ (buy) a new car.',
          answer: 'to buy',
          explanation: 'Use infinitive (to + base form) after "decide".'
        }
      ]
    },
    {
      id: 'p4-topic-13',
      title: 'Prepositions of Place',
      description: 'Learning to use prepositions of place in more complex situations',
      purpose: 'To teach students how to use prepositions of place to describe the position of objects and people in more complex contexts.',
      questions: [
        {
          question: 'The book is _____ the table.',
          answer: 'on',
          explanation: 'Use "on" when something is touching the surface of another object.'
        },
        {
          question: 'The cat is _____ the box.',
          answer: 'in',
          explanation: 'Use "in" when something is inside a container or enclosed space.'
        },
        {
          question: 'The ball is _____ the chair.',
          answer: 'under',
          explanation: 'Use "under" when something is below another object.'
        },
        {
          question: 'The car is _____ the house.',
          answer: 'in front of',
          explanation: 'Use "in front of" when something is ahead of another object.'
        },
        {
          question: 'The dog is _____ the tree.',
          answer: 'behind',
          explanation: 'Use "behind" when something is at the back of another object.'
        },
        {
          question: 'The picture is _____ the wall.',
          answer: 'on',
          explanation: 'Use "on" when something is attached to a surface.'
        },
        {
          question: 'The fish is _____ the water.',
          answer: 'in',
          explanation: 'Use "in" when something is inside a liquid or substance.'
        },
        {
          question: 'The bird is _____ the cage.',
          answer: 'in',
          explanation: 'Use "in" when something is inside a container or enclosed space.'
        },
        {
          question: 'The boy is _____ the door.',
          answer: 'behind',
          explanation: 'Use "behind" when something is at the back of another object.'
        },
        {
          question: 'The cup is _____ the table.',
          answer: 'on',
          explanation: 'Use "on" when something is touching the surface of another object.'
        }
      ]
    },
    {
      id: 'p4-topic-14',
      title: 'Modal Verbs of Obligation',
      description: 'Learning to use modal verbs to express obligation (must, have to, should)',
      purpose: 'To teach students how to use modal verbs to express different levels of obligation and necessity.',
      questions: [
        {
          question: 'You _____ wear a helmet when cycling.',
          answer: 'must',
          explanation: 'Use "must" to express strong obligation or necessity.'
        },
        {
          question: 'I _____ go to school every day.',
          answer: 'have to',
          explanation: 'Use "have to" to express obligation or necessity.'
        },
        {
          question: 'You _____ study hard for the exam.',
          answer: 'should',
          explanation: 'Use "should" to give advice or make recommendations.'
        },
        {
          question: 'She _____ finish her homework before dinner.',
          answer: 'must',
          explanation: 'Use "must" to express strong obligation or necessity.'
        },
        {
          question: 'We _____ be quiet in the library.',
          answer: 'have to',
          explanation: 'Use "have to" to express obligation or necessity.'
        },
        {
          question: 'You _____ eat more vegetables.',
          answer: 'should',
          explanation: 'Use "should" to give advice about healthy eating.'
        },
        {
          question: 'He _____ wear a seatbelt in the car.',
          answer: 'must',
          explanation: 'Use "must" to express strong obligation or necessity.'
        },
        {
          question: 'They _____ arrive on time for the meeting.',
          answer: 'have to',
          explanation: 'Use "have to" to express obligation or necessity.'
        },
        {
          question: 'You _____ drink plenty of water.',
          answer: 'should',
          explanation: 'Use "should" to give advice about health.'
        },
        {
          question: 'I _____ submit my assignment by Friday.',
          answer: 'must',
          explanation: 'Use "must" to express strong obligation or necessity.'
        }
      ]
    },
    {
      id: 'p4-topic-15',
      title: 'Adjectives and Adverbs',
      description: 'Learning to use adjectives and adverbs correctly in sentences',
      purpose: 'To teach students how to use adjectives to describe nouns and adverbs to describe verbs, adjectives, or other adverbs.',
      questions: [
        {
          question: 'She is a _____ (beautiful) girl.',
          answer: 'beautiful',
          explanation: 'Use an adjective to describe a noun (girl).'
        },
        {
          question: 'He runs _____ (quick).',
          answer: 'quickly',
          explanation: 'Use an adverb to describe a verb (runs).'
        },
        {
          question: 'The food tastes _____ (delicious).',
          answer: 'delicious',
          explanation: 'Use an adjective to describe a noun (food).'
        },
        {
          question: 'She sings _____ (sweet).',
          answer: 'sweetly',
          explanation: 'Use an adverb to describe a verb (sings).'
        },
        {
          question: 'The weather is _____ (cold) today.',
          answer: 'cold',
          explanation: 'Use an adjective to describe a noun (weather).'
        },
        {
          question: 'He works _____ (hard).',
          answer: 'hard',
          explanation: 'Use an adverb to describe a verb (works).'
        },
        {
          question: 'The movie was _____ (exciting).',
          answer: 'exciting',
          explanation: 'Use an adjective to describe a noun (movie).'
        },
        {
          question: 'She dances _____ (graceful).',
          answer: 'gracefully',
          explanation: 'Use an adverb to describe a verb (dances).'
        },
        {
          question: 'The book is _____ (interesting).',
          answer: 'interesting',
          explanation: 'Use an adjective to describe a noun (book).'
        },
        {
          question: 'He speaks _____ (clear).',
          answer: 'clearly',
          explanation: 'Use an adverb to describe a verb (speaks).'
        }
      ]
    },
    {
      id: 'p4-topic-16',
      title: 'Conjunctions',
      description: 'Learning to use conjunctions to connect words, phrases, and sentences',
      purpose: 'To teach students how to use conjunctions to connect ideas and create compound and complex sentences.',
      questions: [
        {
          question: 'Complete: "I like apples _____ oranges."',
          answer: 'and',
          explanation: 'Use "and" to connect similar ideas or add information.'
        },
        {
          question: 'Fill in the blank: "I am tired _____ I want to sleep."',
          answer: 'so',
          explanation: 'Use "so" to show cause and effect relationship.'
        },
        {
          question: 'Choose the correct conjunction: "I like tea _____ I don\'t like coffee."',
          answer: 'but',
          explanation: 'Use "but" to show contrast between two ideas.'
        },
        {
          question: 'Complete: "Do you want tea _____ coffee?"',
          answer: 'or',
          explanation: 'Use "or" to show a choice between two options.'
        },
        {
          question: 'Fill in the blank: "I am hungry _____ I will eat."',
          answer: 'so',
          explanation: 'Use "so" to show the result of being hungry.'
        },
        {
          question: 'Choose the correct conjunction: "I like cats _____ dogs."',
          answer: 'and',
          explanation: 'Use "and" to connect two things you like.'
        },
        {
          question: 'Complete: "It is raining _____ I will stay home."',
          answer: 'so',
          explanation: 'Use "so" to show the result of rain.'
        },
        {
          question: 'Fill in the blank: "I can swim _____ I can\'t dive."',
          answer: 'but',
          explanation: 'Use "but" to show contrast between abilities.'
        },
        {
          question: 'Choose the correct conjunction: "Would you like milk _____ juice?"',
          answer: 'or',
          explanation: 'Use "or" to offer a choice between two drinks.'
        },
        {
          question: 'Complete: "I am happy _____ I got a new toy."',
          answer: 'because',
          explanation: 'Use "because" to explain the reason for being happy.'
        }
      ]
    },
    {
      id: 'p4-topic-17',
      title: 'Articles',
      description: 'Learning to use articles (a, an, the) correctly in complex sentences',
      purpose: 'To teach students how to use definite and indefinite articles to specify or generalize nouns in complex contexts.',
      questions: [
        {
          question: 'I have _____ apple.',
          answer: 'an',
          explanation: 'Use "an" before words that begin with vowel sounds (a, e, i, o, u).'
        },
        {
          question: '_____ sun is bright.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I see _____ cat.',
          answer: 'a',
          explanation: 'Use "a" before words that begin with consonant sounds.'
        },
        {
          question: '_____ moon is beautiful.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I have _____ orange.',
          answer: 'an',
          explanation: 'Use "an" before words that begin with vowel sounds.'
        },
        {
          question: '_____ book is on the table.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific book.'
        },
        {
          question: 'I want _____ banana.',
          answer: 'a',
          explanation: 'Use "a" before words that begin with consonant sounds.'
        },
        {
          question: '_____ earth is round.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific, unique thing.'
        },
        {
          question: 'I have _____ umbrella.',
          answer: 'an',
          explanation: 'Use "an" before words that begin with vowel sounds.'
        },
        {
          question: '_____ dog is running.',
          answer: 'The',
          explanation: 'Use "the" when referring to a specific dog.'
        }
      ]
    },
    {
      id: 'p4-topic-18',
      title: 'Sentence Structure',
      description: 'Learning to construct complex sentences with proper word order',
      purpose: 'To teach students how to construct complex sentences with proper word order, including compound and complex sentence structures.',
      questions: [
        {
          question: 'Complete: "I _____ a student."',
          answer: 'am',
          explanation: 'Use the correct form of "to be" for the subject "I".'
        },
        {
          question: 'Fill in the blank: "She _____ English."',
          answer: 'speaks',
          explanation: 'Use the correct verb form for third person singular.'
        },
        {
          question: 'Complete: "_____ you like ice cream?"',
          answer: 'Do',
          explanation: 'Use "Do" to form questions with "you".'
        },
        {
          question: 'Fill in the blank: "_____ is your name?"',
          answer: 'What',
          explanation: 'Use "What" to ask about things or names.'
        },
        {
          question: 'Complete: "_____ the door."',
          answer: 'Open',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Fill in the blank: "They _____ football."',
          answer: 'play',
          explanation: 'Use the base form of the verb for plural subjects.'
        },
        {
          question: 'Complete: "_____ do you live?"',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location.'
        },
        {
          question: 'Fill in the blank: "He _____ in Hong Kong."',
          answer: 'lives',
          explanation: 'Use the correct verb form for third person singular.'
        },
        {
          question: 'Complete: "_____ your homework."',
          answer: 'Do',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Fill in the blank: "We _____ to school."',
          answer: 'go',
          explanation: 'Use the base form of the verb for "we".'
        }
      ]
    }
  ]
}; 